import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';

const OpsIdentityVerification = ({ summary, usersOverride = null, loadingOverride = false, selectionLabel = 'Select User' }) => {
  const { orgId } = useParams();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [verification, setVerification] = useState(null);
  const [code, setCode] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (!statusMessage) return;
    const timer = window.setTimeout(() => setStatusMessage(''), 4000);
    return () => window.clearTimeout(timer);
  }, [statusMessage]);

  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(''), 5000);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (usersOverride) {
      setUsers(usersOverride);
      setLoadingUsers(loadingOverride);
      return;
    }
    let mounted = true;
    const loadUsers = async () => {
      setLoadingUsers(true);
      setError('');
      try {
        const response = await api.get(`/acct-mgmt/v1/orgs/${orgId}/users`);
        if (!mounted) return;
        setUsers(response.data?.users || []);
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || 'Failed to load organization users');
      } finally {
        if (mounted) setLoadingUsers(false);
      }
    };
    if (orgId) loadUsers();
    return () => {
      mounted = false;
    };
  }, [orgId, usersOverride, loadingOverride]);

  const selected = useMemo(() => users.find((u) => String(u.id) === String(selectedUser)), [users, selectedUser]);

  const fetchVerification = async (userId) => {
    if (!userId) {
      setVerification(null);
      return;
    }
    try {
      const response = await api.get('/acct-mgmt/v1/identity-verifications', {
        params: { org_id: orgId, user_id: userId },
      });
      const latest = response.data?.verification || null;
      if (latest?.expires_at) {
        const expiresAt = new Date(latest.expires_at);
        if (!Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() < Date.now()) {
          setVerification(null);
          return;
        }
      }
      if (latest?.status && !['pending', 'verified'].includes(latest.status)) {
        setVerification(null);
        return;
      }
      setVerification(latest);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load verification status');
    }
  };

  useEffect(() => {
    if (!selectedUser || !verification || verification.status !== 'pending') return;
    const handle = window.setInterval(() => {
      fetchVerification(selectedUser);
    }, 8000);
    return () => window.clearInterval(handle);
  }, [selectedUser, verification]);

  const handleUserChange = (event) => {
    const next = event.target.value;
    setSelectedUser(next);
    setVerification(null);
    setCode('');
    setStatusMessage('');
    if (next) {
      fetchVerification(next);
    }
  };

  const handleSend = async (method) => {
    if (!selected) return;
    setStatusMessage('');
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/identity-verifications', {
        org_id: orgId,
        user_id: selected.id,
        method,
      });
      setStatusMessage(`Verification ${method === 'link' ? 'link' : 'code'} sent.`);
      setCode('');
      setVerification({
        id: response.data.verification_id,
        method: response.data.method,
        status: response.data.status,
        expires_at: response.data.expires_at,
        created_at: new Date().toISOString(),
      });
      await fetchVerification(selected.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send verification');
    }
  };

  const handleVerifyCode = async () => {
    if (!verification?.id) return;
    if (!code.trim()) {
      setError('Enter the verification code.');
      return;
    }
    setError('');
    setStatusMessage('');
    try {
      await api.post(`/acct-mgmt/v1/identity-verifications/${verification.id}/verify`, { code: code.trim() });
      setStatusMessage('Verification completed.');
      setCode('');
      await fetchVerification(selected.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify code');
    }
  };

  const handleCancel = async () => {
    if (!verification?.id) return;
    setError('');
    setStatusMessage('');
    try {
      await api.post(`/acct-mgmt/v1/identity-verifications/${verification.id}/cancel`);
      setStatusMessage('Verification cancelled.');
      await fetchVerification(selected.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to cancel verification');
    }
  };

  return (
    <div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {statusMessage && <div className="alert alert-success" role="alert">{statusMessage}</div>}
      <div className="mb-3">
        <label className="form-label">{selectionLabel}</label>
        <select className="form-select" value={selectedUser} onChange={handleUserChange}>
          <option value="">Select a user...</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.full_name || user.email} ({user.email})
            </option>
          ))}
        </select>
        {loadingUsers && <div className="text-muted small mt-1">Loading users...</div>}
      </div>
      <div className="d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-outline-primary" disabled={!selected} onClick={() => handleSend('link')}>
          Send Verification Link
        </button>
        <button type="button" className="btn btn-outline-primary" disabled={!selected} onClick={() => handleSend('code')}>
          Send One-Time Code
        </button>
        {verification?.status === 'pending' && (
          <button type="button" className="btn btn-outline-danger" onClick={handleCancel}>
            Cancel Verification
          </button>
        )}
      </div>

      {selected && (
        <div className="mt-3 pt-3 border-top">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Verification Status</div>
            {selectedUser && (
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => fetchVerification(selectedUser)}>
                Refresh Status
              </button>
            )}
          </div>
          {!verification && <div className="text-muted">No unexpired verification on record.</div>}
          {verification && (
            <>
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-muted small">Status</div>
                  <div className={`fw-semibold text-capitalize d-flex align-items-center gap-2 ${verification.status === 'verified' ? 'text-success' : ''}`}>
                    {verification.status === 'verified' && <i className="fas fa-check-circle" aria-hidden="true"></i>}
                    {verification.status}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Method</div>
                  <div className="fw-semibold text-capitalize">{verification.method}</div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Expires</div>
                  <div className="fw-semibold">
                    {verification.expires_at ? new Date(verification.expires_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Created</div>
                  <div className="fw-semibold">
                    {verification.created_at ? new Date(verification.created_at).toLocaleString() : '—'}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">Verified</div>
                  <div className="fw-semibold">
                    {verification.verified_at ? new Date(verification.verified_at).toLocaleString() : '—'}
                  </div>
                </div>
              </div>
              <div className="text-muted small mt-2">Only the most recent verification is shown.</div>
              {verification.method === 'code' && verification.status === 'pending' && (
                <div className="mt-3">
                  <label className="form-label">Enter Code</label>
                  <div className="d-flex flex-wrap gap-2">
                    <input
                      className="form-control"
                      style={{ maxWidth: 180 }}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter code"
                    />
                    <button type="button" className="btn btn-primary" onClick={handleVerifyCode}>
                      Verify
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default OpsIdentityVerification;
